// src/pages/ClientOnboarding.jsx
import React, { useMemo, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
  Alert,
  UncontrolledTooltip,
} from "reactstrap";
import toastr from "toastr";
import withRouter from "../../components/Common/withRouter";

// API hook that now accepts FormData directly
import { useClientOnBoarding } from "../../api/auth";

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import CustomHeader from "../../components/Auth/CustomHeader";
import FormSelectField from "../../components/Common/FormSelectField";
import { searchUkCompanies } from "../../api/uk-companies";
import debounce from "lodash/debounce";
import {
  appendOne,
  buildWireOwnershipControlDeclaration,
} from "./clientOnboardingSubmitHelpers";

function formatAddressFromUkCompany(addr) {
  if (!addr || typeof addr !== "object") return "";
  const parts = [
    addr.premises,
    addr.address_line_1,
    addr.address_line_2,
    addr.locality,
    addr.region,
    addr.postal_code,
  ].filter(Boolean);
  return parts.join(", ");
}

function mapUkCompanyItemToOption(item) {
  return {
    value: item.company_number,
    label: item.title,
    addressSnippet:
      item.address_snippet || formatAddressFromUkCompany(item.address),
  };
}

function normalizeCompanyWebsiteUrl(raw) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed || trimmed === "https://" || trimmed === "http://") {
    return "";
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

function ensureCompanyWebsiteProtocol(raw) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return "https://";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

const emptyOwner = () => ({
  fullName: "",
  percentageOfOwnership: "",
  idFile: null,
  proofOfAddressFile: null,
});

function sumOwnershipPercentages(owners = [], excludeIndex = -1) {
  return owners.reduce((sum, owner, idx) => {
    if (idx === excludeIndex) return sum;
    const n = Number(owner?.percentageOfOwnership);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
}

function getMaxAllowedOwnershipPercent(owners = [], excludeIndex = -1) {
  return Math.max(0, 100 - sumOwnershipPercentages(owners, excludeIndex));
}

const ClientOnBoarding = (props) => {
  document.title = "Client Registration | JobArk";

  const clientOnBoardingMutation = useClientOnBoarding();

  // read query params
  const search = props?.router?.location?.search || "";
  const qp = useMemo(() => new URLSearchParams(search), [search]);
  const qpFullName = qp.get("fullName") || "";
  const qpEmail = qp.get("email") || "";
  const qpPhone = (qp.get("phoneNumber") || "").replace(/^\+/, ""); // digits only for UI

  const fileRequiredPdfOrImage = Yup.mixed()
    .required("Required")
    .test(
      "fileType",
      "Must be a PDF or image",
      (file) =>
        file instanceof File && /(\.pdf|\.jpg|\.jpeg|\.png)$/i.test(file.name)
    );

  const pdfFileRequired = Yup.mixed()
    .required("Required")
    .test(
      "fileType",
      "Must be a PDF",
      (file) => file instanceof File && /\.pdf$/i.test(file.name)
    );

  const directorEntrySchema = Yup.object({
    fullName: Yup.string().required("Required"),
    idFile: fileRequiredPdfOrImage,
    proofOfAddressFile: fileRequiredPdfOrImage,
  });

  const ownerEntrySchema = Yup.object({
    fullName: Yup.string().required("Required"),
    percentageOfOwnership: Yup.number()
      .typeError("Must be a number")
      .min(25, "Must be at least 25%")
      .max(100, "Max 100%")
      .required("Required"),
    idFile: fileRequiredPdfOrImage,
    proofOfAddressFile: fileRequiredPdfOrImage,
  });

  const insurancePolicyFileSchema = Yup.mixed().test(
    "fileType",
    "Must be a PDF or image",
    (file) => file instanceof File && /\.(pdf|jpe?g|png|webp)$/i.test(file.name)
  );

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      role: "client",
      primaryInformation: {
        fullName: qpFullName,
        email: qpEmail,
        phoneNumber: qpPhone, // digits only in UI
        position: "",
      },
      companyInformation: {
        registeredCompanyName: "",
        tradingName: "",
        companyRegistrationNumber: "",
        vatRegistrationNumber: "",
        registeredOfficeAddress: "",
        tradingAddress: "",
        organisationType: "Ltd",
        industrySector: "",
        companyWebsite: "",
        secondChanceHiring: "",
        // TODO(backend): confirm DTO accepts isWorkplaceDisabilityFriendly on companyInformation
        isWorkplaceDisabilityFriendly: "",
      },
      financeBilling: {
        fullName: "",
        email: "",
        phone: "", // digits only in UI
      },
      ownershipControlDeclaration: {
        confirmed: false,
        ownershipCompanyType: "",
        companyOwnershipStructurePDF: null,
        directors: [
          {
            fullName: "",
            idFile: null,
            proofOfAddressFile: null,
          },
        ],
        individualOwners: [
          {
            fullName: "",
            percentageOfOwnership: "",
            idFile: null,
            proofOfAddressFile: null,
          },
        ],
        corporateUltimateOwners: [
          {
            fullName: "",
            percentageOfOwnership: "",
            idFile: null,
            proofOfAddressFile: null,
          },
        ],
      },
      declarationAndConsent: {
        confirmInformationProvidedIsTrueAndAccurate: false,
        agreeTermsAndPrivacyNotice: false,
        consentToReceivingMarketingInformation: false,
      },
      insurance: {
        employersLiability: null,
        publicLiability: null,
        infoAccurate: false,
      },
      healthAndSafetyContact: {
        fullName: "",
        email: "",
        phoneNumber: "", // digits only in UI
      },
    },
    validationSchema: Yup.object({
      role: Yup.string().oneOf(["client"]).required(),
      primaryInformation: Yup.object({
        fullName: Yup.string().required("Please enter full name"),
        email: Yup.string()
          .email("Invalid email")
          .required("Please enter email"),
        phoneNumber: Yup.string()
          .required("Please enter phone number")
          .matches(/^[0-9]+$/, "Digits only")
          .min(10, "Min 10 digits")
          .max(15, "Max 15 digits"),
        position: Yup.string().required("Please enter position"),
      }),
      companyInformation: Yup.object({
        registeredCompanyName: Yup.string().required("Required"),
        tradingName: Yup.string(),
        companyRegistrationNumber: Yup.string().required("Required"),
        vatRegistrationNumber: Yup.string().transform((v) => String(v ?? "").trim()),
        registeredOfficeAddress: Yup.string().required("Required"),
        tradingAddress: Yup.string().required("Required"),
        organisationType: Yup.string()
          .oneOf([
            "Ltd",
            "PLC",
            "Charity",
            "Partnership",
            "Sole Trader",
            "Public Sector",
            "Other",
          ])
          .required("Required"),
        industrySector: Yup.string().required("Required"),
        companyWebsite: Yup.string()
          .transform((value) => normalizeCompanyWebsiteUrl(value))
          .test(
            "valid-url-if-provided",
            "Enter a valid URL",
            (value) => !value || /^https?:\/\//i.test(value)
          ),
        secondChanceHiring: Yup.string()
          .oneOf(["yes", "no", "unsure"])
          .required("Required"),
        isWorkplaceDisabilityFriendly: Yup.string()
          .oneOf(["yes", "no", "unsure"])
          .required("Required"),
      }),
      financeBilling: Yup.object({
        fullName: Yup.string().required("Please enter full name"),
        email: Yup.string()
          .email("Invalid email")
          .required("Please enter email"),
        phone: Yup.string()
          .required("Please enter phone number")
          .matches(/^[0-9]+$/, "Digits only")
          .min(10, "Min 10 digits")
          .max(15, "Max 15 digits"),
      }),
      ownershipControlDeclaration: Yup.object({
        confirmed: Yup.boolean()
          .oneOf([true], "You must confirm")
          .required("Required"),
        ownershipCompanyType: Yup.string()
          .oneOf(["individuals", "corporate"])
          .required("Select ownership type"),
        directors: Yup.array()
          .of(directorEntrySchema)
          .min(1, "Add at least one director"),
        individualOwners: Yup.array().when("ownershipCompanyType", {
          is: "individuals",
          then: (schema) =>
            schema
              .of(ownerEntrySchema)
              .min(1, "Add at least one shareholder")
              .test(
                "totalOwnership",
                "Total ownership must not exceed 100%",
                (owners) => sumOwnershipPercentages(owners) <= 100
              ),
          otherwise: () => Yup.array(),
        }),
        corporateUltimateOwners: Yup.array().when("ownershipCompanyType", {
          is: "corporate",
          then: (schema) =>
            schema
              .of(ownerEntrySchema)
              .min(1, "Add at least one ultimate shareholder")
              .test(
                "totalOwnership",
                "Total ownership must not exceed 100%",
                (owners) => sumOwnershipPercentages(owners) <= 100
              ),
          otherwise: () => Yup.array(),
        }),
        companyOwnershipStructurePDF: Yup.mixed().when("ownershipCompanyType", {
          is: "corporate",
          then: () => pdfFileRequired,
          otherwise: () => Yup.mixed().nullable(),
        }),
      }),
      declarationAndConsent: Yup.object({
        confirmInformationProvidedIsTrueAndAccurate: Yup.boolean()
          .oneOf(
            [true],
            "You must confirm your information is true and accurate"
          )
          .required("Required"),
        agreeTermsAndPrivacyNotice: Yup.boolean()
          .oneOf([true], "You must agree to the terms and privacy notice")
          .required("Required"),
        consentToReceivingMarketingInformation: Yup.boolean(),
      }),
      insurance: Yup.object({
        infoAccurate: Yup.boolean()
          .oneOf([true], "You must confirm info is accurate")
          .required("Required"),
        employersLiability: insurancePolicyFileSchema.required(
          "Upload employers liability policy"
        ),
        publicLiability: insurancePolicyFileSchema.required(
          "Upload public liability policy"
        ),
      }),
      healthAndSafetyContact: Yup.object({
        fullName: Yup.string().required("Please enter full name"),
        email: Yup.string()
          .email("Invalid email")
          .required("Please enter email"),
        phoneNumber: Yup.string()
          .required("Please enter phone number")
          .matches(/^[0-9]+$/, "Digits only")
          .min(10, "Min 10 digits")
          .max(15, "Max 15 digits"),
      }),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        // Normalize phone numbers to include "+"
        const withPlus = (d) => (d ? (d.startsWith("+") ? d : `+${d}`) : d);

        const occ = values.ownershipControlDeclaration;
        const wireOwnership = buildWireOwnershipControlDeclaration(occ);

        const payload = {
          primaryInformation: {
            ...values.primaryInformation,
            phoneNumber: withPlus(values.primaryInformation.phoneNumber),
          },
          financeBilling: {
            fullName: values.financeBilling.fullName,
            email: values.financeBilling.email,
            phone: withPlus(values.financeBilling.phone),
          },
          healthAndSafetyContact: {
            ...values.healthAndSafetyContact,
            phoneNumber: withPlus(values.healthAndSafetyContact.phoneNumber),
          },
        };

        // Build FormData (single request with JSON blobs + files)
        const fd = new FormData();
        fd.append("role", values.role);
        fd.append(
          "primaryInformation",
          JSON.stringify(payload.primaryInformation)
        );
        fd.append(
          "companyInformation",
          JSON.stringify({
            ...values.companyInformation,
            companyWebsite: normalizeCompanyWebsiteUrl(
              values.companyInformation.companyWebsite
            ),
          })
        );
        fd.append("financeBilling", JSON.stringify(payload.financeBilling));
        fd.append("ownershipControlDeclaration", JSON.stringify(wireOwnership));

        fd.append(
          "healthAndSafetyContact",
          JSON.stringify(payload.healthAndSafetyContact)
        );

        fd.append(
          "declarationAndConsent",
          JSON.stringify({
            confirmInformationProvidedIsTrueAndAccurate:
              !!values.declarationAndConsent
                .confirmInformationProvidedIsTrueAndAccurate,
            agreeTermsAndPrivacyNotice:
              !!values.declarationAndConsent.agreeTermsAndPrivacyNotice,
            consentToReceivingMarketingInformation:
              !!values.declarationAndConsent
                .consentToReceivingMarketingInformation,
          })
        );

        fd.append(
          "insurance",
          JSON.stringify({
            infoAccurate: !!values.insurance.infoAccurate,
          })
        );
        appendOne(
          fd,
          "insuranceEmployersliability",
          values.insurance.employersLiability
        );
        appendOne(
          fd,
          "insurancePublicliability",
          values.insurance.publicLiability
        );

        // KYC / ownership files (repeat same field key per file)
        (occ.directors || []).forEach((d) => {
          appendOne(fd, "directorIdFiles", d?.idFile);
          appendOne(fd, "directorProofOfAddressFiles", d?.proofOfAddressFile);
        });

        const ownershipType =
          occ.ownershipCompanyType === "individual"
            ? "individuals"
            : occ.ownershipCompanyType;
        const ownerRows =
          ownershipType === "corporate"
            ? occ.corporateUltimateOwners || []
            : occ.individualOwners || [];
        const idKey =
          ownershipType === "corporate"
            ? "corporateOwnerIdFiles"
            : "individualOwnerIdFiles";
        const poaKey =
          ownershipType === "corporate"
            ? "corporateOwnerProofOfAddressFiles"
            : "individualOwnerProofOfAddressFiles";
        ownerRows.forEach((row) => {
          appendOne(fd, idKey, row?.idFile);
          appendOne(fd, poaKey, row?.proofOfAddressFile);
        });

        if (ownershipType === "corporate") {
          appendOne(
            fd,
            "companyOwnershipStructurePDF",
            occ.companyOwnershipStructurePDF
          );
        }

        // Submit
        clientOnBoardingMutation.mutate(fd, {
          onSuccess: (res) => {
            toastr.success("Client onboarding submitted!");
            resetForm();

            // Optional redirect if API returns token
            if (res?.token) {
              const email = payload.primaryInformation.email;
              const role = values.role;
              props.router.navigate(
                `/verify-otp?email=${email}&role=${role}&token=${res.token}`
              );
            }
          },
          onError: () => {
            // Error toast is handled by global axios interceptor
          },
        });
      } catch (err) {
        console.error("Client onboarding submit prepare error:", err);
        toastr.error("Unexpected error while preparing submission");
      }
    },
  });

  // helpers
  const setNested = (path, val) => validation.setFieldValue(path, val);

  const handleCompanyWebsiteFocus = () => {
    const current = validation.values.companyInformation?.companyWebsite ?? "";
    if (
      !String(current).trim() ||
      !/^https?:\/\//i.test(String(current).trim())
    ) {
      setNested(
        "companyInformation.companyWebsite",
        ensureCompanyWebsiteProtocol(current)
      );
    }
  };

  const handleCompanyWebsiteBlur = () => {
    const normalized = normalizeCompanyWebsiteUrl(
      validation.values.companyInformation?.companyWebsite
    );
    setNested("companyInformation.companyWebsite", normalized || "");
    validation.setFieldTouched("companyInformation.companyWebsite", true);
    validation.validateField("companyInformation.companyWebsite");
  };
  const touchNested = (path) => validation.setFieldTouched(path, true);

  // Helper to get the first error field path
  const getFirstErrorField = (errors) => {
    const visit = (val, path = "") => {
      if (!val) return null;

      // Most common: Yup returns strings for leaf errors
      if (typeof val === "string") return path;

      // Arrays: Yup/Formik errors for field arrays (e.g. directors[0].fullName)
      if (Array.isArray(val)) {
        for (let i = 0; i < val.length; i++) {
          const nextPath = path ? `${path}[${i}]` : `[${i}]`;
          const found = visit(val[i], nextPath);
          if (found) return found;
        }
        return null;
      }

      // Objects: recurse keys
      if (typeof val === "object") {
        for (const key of Object.keys(val)) {
          const nextPath = path ? `${path}.${key}` : key;
          const found = visit(val[key], nextPath);
          if (found) return found;
        }
        return null;
      }

      // Fallback
      return path || null;
    };

    return visit(errors, "");
  };

  // Track last submit count to prevent scrolling during typing
  const lastSubmitCountRef = React.useRef(0);

  // Scroll to first error field on form submission with errors
  useEffect(() => {
    // Only scroll if submitCount actually increased (new submit attempt)
    // and user is not currently typing in an input field
    const isUserTyping =
      document.activeElement?.tagName === "INPUT" ||
      document.activeElement?.tagName === "TEXTAREA" ||
      document.activeElement?.tagName === "SELECT";

    const submitCountIncreased =
      validation.submitCount > lastSubmitCountRef.current;

    if (
      submitCountIncreased &&
      Object.keys(validation.errors).length > 0 &&
      !isUserTyping
    ) {
      lastSubmitCountRef.current = validation.submitCount;

      // Prefer DOM-based "top-most invalid" for accurate UX (matches what user sees)
      const pickTopMost = (nodes) => {
        const els = Array.from(nodes || []).filter((el) => {
          // only pick visible elements
          if (!el) return false;
          if (el.getAttribute && el.getAttribute("aria-hidden") === "true")
            return false;
          return !!(
            el.offsetWidth ||
            el.offsetHeight ||
            el.getClientRects().length
          );
        });
        if (!els.length) return null;
        let best = els[0];
        let bestTop = best.getBoundingClientRect().top;
        for (const el of els) {
          const top = el.getBoundingClientRect().top;
          if (top < bestTop) {
            bestTop = top;
            best = el;
          }
        }
        return best;
      };

      setTimeout(() => {
        // Don't scroll if user started typing after the submit
        if (
          document.activeElement?.tagName === "INPUT" ||
          document.activeElement?.tagName === "TEXTAREA" ||
          document.activeElement?.tagName === "SELECT"
        ) {
          return;
        }

        // 1) Find the top-most invalid input/select/textarea
        const invalidInputs = document.querySelectorAll(
          "input.is-invalid, select.is-invalid, textarea.is-invalid, .react-tel-input input.is-invalid, .react-select-container.is-invalid input"
        );
        let element = pickTopMost(invalidInputs);

        // 2) Fallback: top-most visible invalid feedback block (checkboxes etc.)
        if (!element) {
          const invalidFeedback = document.querySelectorAll(
            ".invalid-feedback.d-block"
          );
          element = pickTopMost(invalidFeedback);
        }

        // 3) Fallback: old path-based method (best effort)
        if (!element) {
          const firstErrorPath = getFirstErrorField(validation.errors);
          if (firstErrorPath) {
            const dotPath = firstErrorPath.replace(/\[(\d+)\]/g, ".$1");
            const nameCandidates = [firstErrorPath, dotPath];
            for (const p of nameCandidates) {
              const found = document.querySelector(`[name="${p}"]`);
              if (found) {
                element = found;
                break;
              }
            }
          }
        }

        if (element) {
          const card = element.closest(".card");
          const scrollTarget = card || element;
          scrollTarget.scrollIntoView({ behavior: "smooth", block: "start" });

          // Focus (only if it's an input-like element)
          if (element.focus && typeof element.focus === "function") {
            setTimeout(() => element.focus(), 150);
          } else {
            const focusable = element.querySelector?.(
              "input, select, textarea"
            );
            if (focusable?.focus) setTimeout(() => focusable.focus(), 150);
          }
          return;
        }

        // Final fallback: scroll to top of form
        const formElement = document.querySelector("form");
        if (formElement)
          formElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } else if (submitCountIncreased) {
      // Update ref even if no errors, so we track submit attempts correctly
      lastSubmitCountRef.current = validation.submitCount;
    }
  }, [validation.submitCount, validation.errors, validation.touched]);

  const addDirector = () => {
    const arr = validation.values.ownershipControlDeclaration.directors || [];
    setNested("ownershipControlDeclaration.directors", [
      ...arr,
      { fullName: "", idFile: null, proofOfAddressFile: null },
    ]);
  };

  const removeDirector = (idx) => {
    const arr = [
      ...(validation.values.ownershipControlDeclaration.directors || []),
    ];
    arr.splice(idx, 1);
    setNested("ownershipControlDeclaration.directors", arr);
  };

  const handleOwnershipTypeChange = (type) => {
    const currentType =
      validation.values.ownershipControlDeclaration.ownershipCompanyType;
    if (currentType === type) return;

    validation.setValues({
      ...validation.values,
      ownershipControlDeclaration: {
        ...validation.values.ownershipControlDeclaration,
        ownershipCompanyType: type,
        companyOwnershipStructurePDF: null,
        individualOwners: type === "individuals" ? [emptyOwner()] : [],
        corporateUltimateOwners: type === "corporate" ? [emptyOwner()] : [],
      },
    });
    validation.setFieldTouched(
      "ownershipControlDeclaration.ownershipCompanyType",
      true,
      false
    );
  };

  const setOwnerPercentage = (path, rawValue) => {
    const arrayPath = path.replace(/\[\d+\]\.percentageOfOwnership$/, "");
    const idxMatch = path.match(/\[(\d+)\]\.percentageOfOwnership$/);
    const ownerIndex = idxMatch ? Number(idxMatch[1]) : -1;
    const owners =
      arrayPath
        .split(".")
        .reduce(
          (obj, key) => (obj != null ? obj[key] : undefined),
          validation.values
        ) || [];

    if (rawValue === "") {
      setNested(path, "");
    } else {
      const num = Number(rawValue);
      if (!Number.isFinite(num)) return;
      const maxAllowed = getMaxAllowedOwnershipPercent(owners, ownerIndex);
      setNested(path, Math.min(Math.max(num, 0), 100, maxAllowed));
    }

    validation.setFieldTouched(arrayPath, true, false);
    validation.validateField(arrayPath);
  };

  const addIndividualOwner = () => {
    const arr =
      validation.values.ownershipControlDeclaration.individualOwners || [];
    setNested("ownershipControlDeclaration.individualOwners", [
      ...arr,
      emptyOwner(),
    ]);
  };

  const removeIndividualOwner = (idx) => {
    const arr = [
      ...(validation.values.ownershipControlDeclaration.individualOwners || []),
    ];
    arr.splice(idx, 1);
    setNested("ownershipControlDeclaration.individualOwners", arr);
  };

  const addCorporateUltimateOwner = () => {
    const arr =
      validation.values.ownershipControlDeclaration.corporateUltimateOwners ||
      [];
    setNested("ownershipControlDeclaration.corporateUltimateOwners", [
      ...arr,
      emptyOwner(),
    ]);
  };

  const removeCorporateUltimateOwner = (idx) => {
    const arr = [
      ...(validation.values.ownershipControlDeclaration
        .corporateUltimateOwners || []),
    ];
    arr.splice(idx, 1);
    setNested("ownershipControlDeclaration.corporateUltimateOwners", arr);
  };

  const copyTradingNameFromRegisteredCompany = () => {
    const n =
      validation.values.companyInformation.registeredCompanyName?.trim() || "";
    setNested("companyInformation.tradingName", n);
    validation.setFieldTouched("companyInformation.tradingName", true, false);
  };

  const copyTradingAddressFromRegisteredOffice = () => {
    const a =
      validation.values.companyInformation.registeredOfficeAddress?.trim() ||
      "";
    setNested("companyInformation.tradingAddress", a);
    validation.setFieldTouched(
      "companyInformation.tradingAddress",
      true,
      false
    );
  };

  const copyPrimaryToHealthAndSafety = () => {
    const { fullName, position, email, phoneNumber } =
      validation.values.primaryInformation || {};
    validation.setFieldValue("healthAndSafetyContact", {
      fullName: fullName || "",
      position: position || "",
      email: email || "",
      phoneNumber: (phoneNumber || "").replace(/^\+/, ""),
    });
    ["fullName", "position", "email", "phoneNumber"].forEach((k) =>
      validation.setFieldTouched(`healthAndSafetyContact.${k}`, true, false)
    );
  };

  const copyPrimaryToFinanceBilling = () => {
    const { fullName, position, email, phoneNumber } =
      validation.values.primaryInformation || {};
    validation.setFieldValue("financeBilling", {
      ...validation.values.financeBilling,
      fullName: fullName || "",
      position: position || "",
      email: email || "",
      phone: (phoneNumber || "").replace(/^\+/, ""),
    });
    ["fullName", "position", "email", "phone"].forEach((k) =>
      validation.setFieldTouched(`financeBilling.${k}`, true, false)
    );
  };

  const isSubmitting = clientOnBoardingMutation.isPending;

  /** AsyncSelect calls loadOptions per keystroke; lodash debounce batches API calls. */
  const ukCompanyPendingResolveRef = useRef(null);

  const debouncedUkCompanySearch = useMemo(
    () =>
      debounce(
        async (q, resolve) => {
          try {
            const items = await searchUkCompanies({
              q,
              items_per_page: 10,
              start_index: 0,
            });
            resolve(items.map(mapUkCompanyItemToOption));
          } catch {
            resolve([]);
          }
        },
        400,
        { leading: false, trailing: true }
      ),
    []
  );

  const loadUkCompanyOptions = useCallback(
    (inputValue) => {
      return new Promise((resolve) => {
        const q = (inputValue || "").trim();

        if (ukCompanyPendingResolveRef.current) {
          ukCompanyPendingResolveRef.current([]);
          ukCompanyPendingResolveRef.current = null;
        }

        if (!q) {
          debouncedUkCompanySearch.cancel();
          resolve([]);
          return;
        }

        ukCompanyPendingResolveRef.current = resolve;
        debouncedUkCompanySearch(q, resolve);
      });
    },
    [debouncedUkCompanySearch]
  );

  useEffect(() => {
    return () => {
      debouncedUkCompanySearch.cancel();
      if (ukCompanyPendingResolveRef.current) {
        ukCompanyPendingResolveRef.current([]);
        ukCompanyPendingResolveRef.current = null;
      }
    };
  }, [debouncedUkCompanySearch]);

  const registeredCompanySelectValue = useMemo(() => {
    const ci = validation.values.companyInformation;
    const name = (ci.registeredCompanyName || "").trim();
    if (!name) return null;
    return {
      value: ci.companyRegistrationNumber || name,
      label: name,
      addressSnippet: ci.registeredOfficeAddress || "",
    };
  }, [validation.values.companyInformation]);

  const ukCompanyInputRef = useRef("");

  const handleUkCompanyInputChange = useCallback((inputValue, meta) => {
    if (meta?.action === "input-change") {
      ukCompanyInputRef.current = inputValue ?? "";
    }
  }, []);

  const handleRegisteredCompanyBlur = useCallback(() => {
    validation.setFieldTouched(
      "companyInformation.registeredCompanyName",
      true,
      false
    );
    const typed = (ukCompanyInputRef.current || "").trim();
    const currentName = (
      validation.values.companyInformation.registeredCompanyName || ""
    ).trim();
    if (typed && typed !== currentName) {
      validation.setValues(
        (prev) => ({
          ...prev,
          companyInformation: {
            ...prev.companyInformation,
            registeredCompanyName: typed,
            companyRegistrationNumber: "",
            registeredOfficeAddress: "",
          },
        }),
        true
      );
      validation.setTouched(
        {
          ...validation.touched,
          companyInformation: {
            ...(validation.touched.companyInformation || {}),
            registeredCompanyName: true,
            companyRegistrationNumber: true,
            registeredOfficeAddress: true,
          },
        },
        false
      );
    }
    ukCompanyInputRef.current = "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRegisteredCompanySelect = useCallback((option) => {
    ukCompanyInputRef.current = "";

    const applyCompanyFields = (companyPartial) => {
      validation.setValues(
        (prev) => ({
          ...prev,
          companyInformation: {
            ...prev.companyInformation,
            ...companyPartial,
          },
        }),
        true
      );
      validation.setTouched(
        {
          ...validation.touched,
          companyInformation: {
            ...(validation.touched.companyInformation || {}),
            registeredCompanyName: true,
            companyRegistrationNumber: true,
            registeredOfficeAddress: true,
          },
        },
        false
      );
    };

    if (!option) {
      applyCompanyFields({
        registeredCompanyName: "",
        companyRegistrationNumber: "",
        registeredOfficeAddress: "",
      });
      return;
    }
    if (option.__isNew__) {
      const name = String(option.label ?? option.value ?? "").trim();
      applyCompanyFields({
        registeredCompanyName: name,
        companyRegistrationNumber: "",
        registeredOfficeAddress: "",
      });
      return;
    }
    applyCompanyFields({
      registeredCompanyName: option.label || "",
      companyRegistrationNumber: String(option.value ?? ""),
      registeredOfficeAddress: option.addressSnippet || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      <CustomHeader />
      <div
        className="newsletter-page client-onboarding-page mt-5"
        style={{ backgroundColor: "#F8F8F8", minHeight: "100vh" }}
      >
        <div className="account-pages my-3 ">
          <Container>
            <Row className="justify-content-center">
              <Col md={12} lg={10} xl={9}>
                <div>
                  <div className="p-4 text-center">
                    <h2
                      className="text-primary mb-2"
                      style={{ fontSize: "24px", fontWeight: "bold" }}
                    >
                      Client Registration
                    </h2>
                    <p
                      className="text-primary mb-0"
                      style={{ fontSize: "16px" }}
                    >
                      Please complete the form below to register your business
                      and get started with jobark
                    </p>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={12} lg={10} xl={9}>
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    return false;
                  }}
                >
                  {clientOnBoardingMutation.isSuccess && (
                    <Alert color="success">Submitted successfully.</Alert>
                  )}
                  {clientOnBoardingMutation.isError && (
                    <Alert color="danger">Something went wrong.</Alert>
                  )}

                  {/* ============ Company Information ============ */}
                  <Card
                    className="mb-3"
                    style={{
                      backgroundColor: "#FFFFFF",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      borderRadius: "6px",
                      border: "1px solid #E0E0E0",
                    }}
                  >
                    <CardHeader
                      style={{
                        backgroundColor: "#E8E8E8",
                        borderBottom: "1px solid #E0E0E0",
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <h5
                          className="mb-0"
                          style={{
                            fontSize: "18px",
                            fontWeight: "bold",
                            color: "#333333",
                          }}
                        >
                          Company/Organisation Details
                        </h5>
                        <span
                          id="company-details-tooltip"
                          style={{
                            cursor: "pointer",
                            color: "#6C757D",
                            fontSize: "18px",
                          }}
                        >
                          <i
                            className="mdi mdi-help-circle-outline"
                            aria-hidden
                          />
                        </span>
                      </div>
                    </CardHeader>
                    <CardBody style={{ backgroundColor: "#F5F5F5" }}>
                      <UncontrolledTooltip
                        placement="top"
                        target="company-details-tooltip"
                      >
                        Enter your official company or organisation details
                        below. We will use this information to verify your
                        business and set up your JobArk account
                      </UncontrolledTooltip>
                      <Row className="align-items-start">
                        <Col md={6} className="mb-3">
                          <FormSelectField
                            async
                            creatable
                            allowCreateWhileLoading
                            name="companyInformation.registeredCompanyName"
                            label="Registered Company Name"
                            placeholder="Search or type your company name"
                            formatCreateLabel={(inputValue) =>
                              `Use "${inputValue}" (manual entry)`
                            }
                            error={
                              validation.errors.companyInformation
                                ?.registeredCompanyName
                            }
                            invalid={
                              validation.touched.companyInformation
                                ?.registeredCompanyName &&
                              !!validation.errors.companyInformation
                                ?.registeredCompanyName
                            }
                            loadOptions={loadUkCompanyOptions}
                            defaultOptions={false}
                            cacheOptions
                            value={registeredCompanySelectValue}
                            onChange={handleRegisteredCompanySelect}
                            onInputChange={handleUkCompanyInputChange}
                            onBlur={handleRegisteredCompanyBlur}
                            isClearable
                            blurInputOnSelect
                            noOptionsMessage={({ inputValue }) =>
                              (inputValue || "").trim()
                                ? "No companies found"
                                : "Type to search or enter manually"
                            }
                          />
                        </Col>
                        <Col md={6} className="mb-3">
                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-1">
                            <Label
                              className="mb-1"
                              style={{
                                fontSize: "14px",
                                fontWeight: "normal",
                                color: "#333333",
                              }}
                            >
                              Trading Name
                            </Label>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary mb-1"
                              onClick={copyTradingNameFromRegisteredCompany}
                            >
                              Copy from company name
                            </button>
                          </div>
                          <Input
                            name="companyInformation.tradingName"
                            value={
                              validation.values.companyInformation.tradingName
                            }
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.companyInformation
                                ?.tradingName &&
                              !!validation.errors.companyInformation
                                ?.tradingName
                            }
                          />
                          <FormFeedback>
                            {validation.errors.companyInformation?.tradingName}
                          </FormFeedback>
                        </Col>

                        <Col md={6} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                            }}
                          >
                            Company Registration Number
                          </Label>
                          <Input
                            name="companyInformation.companyRegistrationNumber"
                            value={
                              validation.values.companyInformation
                                .companyRegistrationNumber
                            }
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.companyInformation
                                ?.companyRegistrationNumber &&
                              !!validation.errors.companyInformation
                                ?.companyRegistrationNumber
                            }
                          />
                          <FormFeedback>
                            {
                              validation.errors.companyInformation
                                ?.companyRegistrationNumber
                            }
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                            }}
                          >
                            VAT Registration Number
                          </Label>
                          <Input
                            name="companyInformation.vatRegistrationNumber"
                            value={
                              validation.values.companyInformation
                                .vatRegistrationNumber
                            }
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.companyInformation
                                ?.vatRegistrationNumber &&
                              !!validation.errors.companyInformation
                                ?.vatRegistrationNumber
                            }
                          />
                          <FormFeedback>
                            {
                              validation.errors.companyInformation
                                ?.vatRegistrationNumber
                            }
                          </FormFeedback>
                        </Col>

                        <Col md={6} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                            }}
                          >
                            Registered Office Address
                          </Label>
                          <Input
                            name="companyInformation.registeredOfficeAddress"
                            value={
                              validation.values.companyInformation
                                .registeredOfficeAddress
                            }
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.companyInformation
                                ?.registeredOfficeAddress &&
                              !!validation.errors.companyInformation
                                ?.registeredOfficeAddress
                            }
                          />
                          <FormFeedback>
                            {
                              validation.errors.companyInformation
                                ?.registeredOfficeAddress
                            }
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-1">
                            <Label
                              className="mb-1"
                              style={{
                                fontSize: "14px",
                                fontWeight: "normal",
                                color: "#333333",
                              }}
                            >
                              Trading Address *
                            </Label>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary mb-1"
                              onClick={copyTradingAddressFromRegisteredOffice}
                            >
                              Copy from registered office
                            </button>
                          </div>
                          <Input
                            name="companyInformation.tradingAddress"
                            value={
                              validation.values.companyInformation
                                .tradingAddress
                            }
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.companyInformation
                                ?.tradingAddress &&
                              !!validation.errors.companyInformation
                                ?.tradingAddress
                            }
                          />
                          <FormFeedback>
                            {
                              validation.errors.companyInformation
                                ?.tradingAddress
                            }
                          </FormFeedback>
                        </Col>

                        <Col md={6} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                            }}
                          >
                            Organisation Type
                          </Label>
                          <Input
                            type="select"
                            name="companyInformation.organisationType"
                            value={
                              validation.values.companyInformation
                                .organisationType
                            }
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.companyInformation
                                ?.organisationType &&
                              !!validation.errors.companyInformation
                                ?.organisationType
                            }
                          >
                            {[
                              "Ltd",
                              "PLC",
                              "Charity",
                              "Partnership",
                              "Sole Trader",
                              "Public Sector",
                              "Other",
                            ].map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </Input>
                          <FormFeedback>
                            {
                              validation.errors.companyInformation
                                ?.organisationType
                            }
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                            }}
                          >
                            Industry Sector
                          </Label>
                          <Input
                            name="companyInformation.industrySector"
                            value={
                              validation.values.companyInformation
                                .industrySector
                            }
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.companyInformation
                                ?.industrySector &&
                              !!validation.errors.companyInformation
                                ?.industrySector
                            }
                          />
                          <FormFeedback>
                            {
                              validation.errors.companyInformation
                                ?.industrySector
                            }
                          </FormFeedback>
                        </Col>

                        <Col md={12} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                            }}
                          >
                            Company Website
                          </Label>
                          <Input
                            type="text"
                            name="companyInformation.companyWebsite"
                            value={
                              validation.values.companyInformation
                                .companyWebsite
                            }
                            onChange={validation.handleChange}
                            onFocus={handleCompanyWebsiteFocus}
                            onBlur={handleCompanyWebsiteBlur}
                            placeholder="example.com"
                            autoComplete="url"
                            invalid={
                              validation.touched.companyInformation
                                ?.companyWebsite &&
                              !!validation.errors.companyInformation
                                ?.companyWebsite
                            }
                          />
                          <FormFeedback>
                            {
                              validation.errors.companyInformation
                                ?.companyWebsite
                            }
                          </FormFeedback>
                        </Col>

                        <Col sm={6} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                              minHeight: "52px",
                            }}
                          >
                            Is your workplace disability friendly? *
                          </Label>
                          <Input
                            type="select"
                            name="companyInformation.isWorkplaceDisabilityFriendly"
                            value={
                              validation.values.companyInformation
                                .isWorkplaceDisabilityFriendly || ""
                            }
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.companyInformation
                                ?.isWorkplaceDisabilityFriendly &&
                              !!validation.errors.companyInformation
                                ?.isWorkplaceDisabilityFriendly
                            }
                          >
                            <option value="">Select…</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                            <option value="unsure">Unsure</option>
                          </Input>
                          <FormFeedback>
                            {
                              validation.errors.companyInformation
                                ?.isWorkplaceDisabilityFriendly
                            }
                          </FormFeedback>
                        </Col>

                        <Col sm={6} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                              minHeight: "52px",
                            }}
                          >
                            JobArk supports second chance hiring and ban the
                            box, do you? *
                          </Label>
                          <Input
                            type="select"
                            name="companyInformation.secondChanceHiring"
                            value={
                              validation.values.companyInformation
                                .secondChanceHiring || ""
                            }
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.companyInformation
                                ?.secondChanceHiring &&
                              !!validation.errors.companyInformation
                                ?.secondChanceHiring
                            }
                          >
                            <option value="">Select…</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                            <option value="unsure">Unsure</option>
                          </Input>
                          <FormFeedback>
                            {
                              validation.errors.companyInformation
                                ?.secondChanceHiring
                            }
                          </FormFeedback>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* ============ Primary Information ============ */}
                  <Card
                    className="mb-3"
                    style={{
                      backgroundColor: "#FFFFFF",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      borderRadius: "6px",
                      border: "1px solid #E0E0E0",
                    }}
                  >
                    <CardHeader
                      style={{
                        backgroundColor: "#E8E8E8",
                        borderBottom: "1px solid #E0E0E0",
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <h5
                          className="mb-0"
                          style={{
                            fontSize: "18px",
                            fontWeight: "bold",
                            color: "#333333",
                          }}
                        >
                          Primary Contact Information
                        </h5>
                        <span
                          id="primary-contact-tooltip"
                          style={{
                            cursor: "pointer",
                            color: "#6C757D",
                            fontSize: "18px",
                          }}
                        >
                          <i
                            className="mdi mdi-help-circle-outline"
                            aria-hidden
                          />
                        </span>
                      </div>
                    </CardHeader>
                    <CardBody style={{ backgroundColor: "#F5F5F5" }}>
                      <UncontrolledTooltip
                        placement="top"
                        target="primary-contact-tooltip"
                      >
                        The main person responsible for your JobArk account
                      </UncontrolledTooltip>
                      <Row>
                        <Col md={6} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                            }}
                          >
                            Full Name *
                          </Label>
                          <Input
                            name="primaryInformation.fullName"
                            value={
                              validation.values.primaryInformation.fullName
                            }
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.primaryInformation?.fullName &&
                              !!validation.errors.primaryInformation?.fullName
                            }
                            placeholder="Enter Full Name"
                          />
                          <FormFeedback>
                            {validation.errors.primaryInformation?.fullName}
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                            }}
                          >
                            Email *
                          </Label>
                          <Input
                            name="primaryInformation.email"
                            type="email"
                            value={validation.values.primaryInformation.email}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.primaryInformation?.email &&
                              !!validation.errors.primaryInformation?.email
                            }
                            placeholder="Enter Email"
                          />
                          <FormFeedback>
                            {validation.errors.primaryInformation?.email}
                          </FormFeedback>
                          <small className="text-muted d-block mt-1">
                            This email will need to be verified after you
                            submit.
                          </small>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                            }}
                          >
                            Phone Number *
                          </Label>
                          <PhoneInput
                            country="gb"
                            enableSearch
                            countryCodeEditable={false}
                            containerClass="w-100"
                            inputProps={{
                              name: "primaryInformation.phoneNumber",
                            }}
                            inputClass={
                              validation.touched.primaryInformation
                                ?.phoneNumber &&
                              validation.errors.primaryInformation?.phoneNumber
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            value={
                              validation.values.primaryInformation.phoneNumber
                            }
                            onBlur={() =>
                              touchNested("primaryInformation.phoneNumber")
                            }
                            onChange={(v) =>
                              setNested("primaryInformation.phoneNumber", v)
                            }
                          />
                          {validation.touched.primaryInformation?.phoneNumber &&
                            validation.errors.primaryInformation
                              ?.phoneNumber && (
                              <div className="invalid-feedback d-block">
                                {
                                  validation.errors.primaryInformation
                                    ?.phoneNumber
                                }
                              </div>
                            )}
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                            }}
                          >
                            Position *
                          </Label>
                          <Input
                            name="primaryInformation.position"
                            value={
                              validation.values.primaryInformation.position
                            }
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.primaryInformation?.position &&
                              !!validation.errors.primaryInformation?.position
                            }
                          />
                          <FormFeedback>
                            {validation.errors.primaryInformation?.position}
                          </FormFeedback>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* ============ Health & Safety Contact (NEW) ============ */}
                  <Card
                    className="mb-3"
                    style={{
                      backgroundColor: "#FFFFFF",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      borderRadius: "6px",
                      border: "1px solid #E0E0E0",
                    }}
                  >
                    <CardHeader
                      className="d-flex justify-content-between align-items-center"
                      style={{
                        backgroundColor: "#E8E8E8",
                        borderBottom: "1px solid #E0E0E0",
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <h5
                          className="mb-0"
                          style={{
                            fontSize: "18px",
                            fontWeight: "bold",
                            color: "#333333",
                          }}
                        >
                          Health &amp; Safety Contact
                        </h5>
                        <span
                          id="health-safety-tooltip"
                          style={{
                            cursor: "pointer",
                            color: "#6C757D",
                            fontSize: "18px",
                          }}
                        >
                          <i
                            className="mdi mdi-help-circle-outline"
                            aria-hidden
                          />
                        </span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={copyPrimaryToHealthAndSafety}
                      >
                        Copy primary contact information
                      </button>
                    </CardHeader>
                    <CardBody style={{ backgroundColor: "#F5F5F5" }}>
                      <UncontrolledTooltip
                        placement="top"
                        target="health-safety-tooltip"
                      >
                        The person responsible for workplace safety and site
                        compliance within your organisation
                      </UncontrolledTooltip>
                      <Row>
                        <Col md={6} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                            }}
                          >
                            Full Name *
                          </Label>
                          <Input
                            name="healthAndSafetyContact.fullName"
                            value={
                              validation.values.healthAndSafetyContact.fullName
                            }
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.healthAndSafetyContact
                                ?.fullName &&
                              !!validation.errors.healthAndSafetyContact
                                ?.fullName
                            }
                            placeholder="Enter Full Name"
                          />
                          <FormFeedback>
                            {validation.errors.healthAndSafetyContact?.fullName}
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                            }}
                          >
                            Email *
                          </Label>
                          <Input
                            name="healthAndSafetyContact.email"
                            type="email"
                            value={
                              validation.values.healthAndSafetyContact.email
                            }
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.healthAndSafetyContact
                                ?.email &&
                              !!validation.errors.healthAndSafetyContact?.email
                            }
                            placeholder="Enter Email"
                          />
                          <FormFeedback>
                            {validation.errors.healthAndSafetyContact?.email}
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                            }}
                          >
                            Phone Number *
                          </Label>
                          <PhoneInput
                            country="gb"
                            enableSearch
                            countryCodeEditable={false}
                            containerClass="w-100"
                            inputProps={{
                              name: "healthAndSafetyContact.phoneNumber",
                            }}
                            inputClass={
                              validation.touched.healthAndSafetyContact
                                ?.phoneNumber &&
                              validation.errors.healthAndSafetyContact
                                ?.phoneNumber
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            value={
                              validation.values.healthAndSafetyContact
                                .phoneNumber
                            }
                            onBlur={() =>
                              touchNested("healthAndSafetyContact.phoneNumber")
                            }
                            onChange={(v) =>
                              setNested("healthAndSafetyContact.phoneNumber", v)
                            }
                          />
                          {validation.touched.healthAndSafetyContact
                            ?.phoneNumber &&
                            validation.errors.healthAndSafetyContact
                              ?.phoneNumber && (
                              <div className="invalid-feedback d-block">
                                {
                                  validation.errors.healthAndSafetyContact
                                    ?.phoneNumber
                                }
                              </div>
                            )}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* ============ Finance & Billing ============ */}
                  <Card
                    className="mb-3"
                    style={{
                      backgroundColor: "#FFFFFF",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      borderRadius: "6px",
                      border: "1px solid #E0E0E0",
                    }}
                  >
                    <CardHeader
                      className="d-flex justify-content-between align-items-center"
                      style={{
                        backgroundColor: "#E8E8E8",
                        borderBottom: "1px solid #E0E0E0",
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <h5
                          className="mb-0"
                          style={{
                            fontSize: "18px",
                            fontWeight: "bold",
                            color: "#333333",
                          }}
                        >
                          Finance &amp; Billing Contact
                        </h5>
                        <span
                          id="finance-billing-tooltip"
                          style={{
                            cursor: "pointer",
                            color: "#6C757D",
                            fontSize: "18px",
                          }}
                        >
                          <i
                            className="mdi mdi-help-circle-outline"
                            aria-hidden
                          />
                        </span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={copyPrimaryToFinanceBilling}
                      >
                        Copy primary contact information
                      </button>
                    </CardHeader>
                    <CardBody style={{ backgroundColor: "#F5F5F5" }}>
                      <UncontrolledTooltip
                        placement="top"
                        target="finance-billing-tooltip"
                      >
                        Please provide the details of the person responsible for
                        invoice, payments and finance enquiries, this helps us
                        ensure billing and payment matters are directed to the
                        correct contact
                      </UncontrolledTooltip>

                      <Row>
                        <Col md={6} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                            }}
                          >
                            Finance Contact Full Name *
                          </Label>
                          <Input
                            name="financeBilling.fullName"
                            value={validation.values.financeBilling.fullName}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.financeBilling?.fullName &&
                              !!validation.errors.financeBilling?.fullName
                            }
                            placeholder="Enter Full Name"
                          />
                          <FormFeedback>
                            {validation.errors.financeBilling?.fullName}
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                            }}
                          >
                            Finance Email *
                          </Label>
                          <Input
                            name="financeBilling.email"
                            type="email"
                            value={validation.values.financeBilling.email}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.financeBilling?.email &&
                              !!validation.errors.financeBilling?.email
                            }
                            placeholder="Enter Email"
                          />
                          <FormFeedback>
                            {validation.errors.financeBilling?.email}
                          </FormFeedback>
                        </Col>
                        <Col md={6} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                            }}
                          >
                            Finance Phone *
                          </Label>
                          <PhoneInput
                            country="gb"
                            enableSearch
                            countryCodeEditable={false}
                            containerClass="w-100"
                            inputProps={{ name: "financeBilling.phone" }}
                            inputClass={
                              validation.touched.financeBilling?.phone &&
                              validation.errors.financeBilling?.phone
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            value={validation.values.financeBilling.phone}
                            onBlur={() => touchNested("financeBilling.phone")}
                            onChange={(v) =>
                              setNested("financeBilling.phone", v)
                            }
                          />
                          {validation.touched.financeBilling?.phone &&
                            validation.errors.financeBilling?.phone && (
                              <div className="invalid-feedback d-block">
                                {validation.errors.financeBilling?.phone}
                              </div>
                            )}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* ============ Insurance ============ */}
                  <Card
                    className="mb-3"
                    style={{
                      backgroundColor: "#FFFFFF",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      borderRadius: "6px",
                      border: "1px solid #E0E0E0",
                    }}
                  >
                    <CardHeader
                      style={{
                        backgroundColor: "#E8E8E8",
                        borderBottom: "1px solid #E0E0E0",
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <h5
                          className="mb-0"
                          style={{
                            fontSize: "18px",
                            fontWeight: "bold",
                            color: "#333333",
                          }}
                        >
                          Insurance
                        </h5>
                        <span
                          id="insurance-tooltip"
                          style={{
                            cursor: "pointer",
                            color: "#6C757D",
                            fontSize: "18px",
                          }}
                        >
                          <i
                            className="mdi mdi-help-circle-outline"
                            aria-hidden
                          />
                        </span>
                      </div>
                    </CardHeader>
                    <CardBody style={{ backgroundColor: "#F5F5F5" }}>
                      <UncontrolledTooltip
                        placement="top"
                        target="insurance-tooltip"
                      >
                        Please provide your insurance certificates. This helps
                        JobArk verify that appropriate insurance is in place
                        before supplying workforce.
                      </UncontrolledTooltip>
                      <Row>
                        <Col md={6} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                            }}
                          >
                            Employers Liability (PDF/Image) *
                          </Label>
                          <Input
                            type="file"
                            accept=".pdf,image/*"
                            onChange={(e) => {
                              const f = e.currentTarget.files?.[0];
                              setNested(
                                "insurance.employersLiability",
                                f || null
                              );
                            }}
                            onBlur={() =>
                              touchNested("insurance.employersLiability")
                            }
                          />
                          {validation.touched.insurance?.employersLiability &&
                            validation.errors.insurance?.employersLiability && (
                              <div className="invalid-feedback d-block">
                                {
                                  validation.errors.insurance
                                    ?.employersLiability
                                }
                              </div>
                            )}
                        </Col>

                        <Col md={6} className="mb-3">
                          <Label
                            style={{
                              fontSize: "14px",
                              fontWeight: "normal",
                              color: "#333333",
                              marginBottom: "5px",
                            }}
                          >
                            Public Liability (PDF/Image) *
                          </Label>
                          <Input
                            type="file"
                            accept=".pdf,image/*"
                            onChange={(e) => {
                              const f = e.currentTarget.files?.[0];
                              setNested("insurance.publicLiability", f || null);
                            }}
                            onBlur={() =>
                              touchNested("insurance.publicLiability")
                            }
                          />
                          {validation.touched.insurance?.publicLiability &&
                            validation.errors.insurance?.publicLiability && (
                              <div className="invalid-feedback d-block">
                                {validation.errors.insurance?.publicLiability}
                              </div>
                            )}
                        </Col>

                        <Col md={12} className="mb-3">
                          <div className="form-check">
                            <Input
                              id="insuranceInfoAccurate"
                              className="form-check-input"
                              type="checkbox"
                              checked={validation.values.insurance.infoAccurate}
                              onChange={(e) =>
                                setNested(
                                  "insurance.infoAccurate",
                                  e.target.checked
                                )
                              }
                              onBlur={() =>
                                validation.setFieldTouched(
                                  "insurance.infoAccurate",
                                  true
                                )
                              }
                              invalid={
                                validation.touched.insurance?.infoAccurate &&
                                !!validation.errors.insurance?.infoAccurate
                              }
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="insuranceInfoAccurate"
                            >
                              I confirm the insurance information provided is
                              accurate.
                            </Label>
                          </div>
                          <div className="invalid-feedback d-block">
                            {validation.touched.insurance?.infoAccurate &&
                              validation.errors.insurance?.infoAccurate}
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* ============ Know your client information ============ */}
                  <Card
                    className="mb-3"
                    style={{
                      backgroundColor: "#FFFFFF",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      borderRadius: "6px",
                      border: "1px solid #E0E0E0",
                    }}
                  >
                    <CardHeader
                      style={{
                        backgroundColor: "#E8E8E8",
                        borderBottom: "1px solid #E0E0E0",
                      }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <h5
                          className="mb-0"
                          style={{
                            fontSize: "18px",
                            fontWeight: "bold",
                            color: "#333333",
                          }}
                        >
                          Know Your Client Information
                        </h5>
                        <span
                          id="know-client-tooltip"
                          style={{
                            cursor: "pointer",
                            color: "#6C757D",
                            fontSize: "18px",
                          }}
                        >
                          <i
                            className="mdi mdi-help-circle-outline"
                            aria-hidden
                          />
                        </span>
                      </div>
                    </CardHeader>
                    <CardBody style={{ backgroundColor: "#F5F5F5" }}>
                      <UncontrolledTooltip
                        placement="top"
                        target="know-client-tooltip"
                      >
                        The information in this section is used to verify your
                        business identity and support our compliance and due
                        diligence processes.
                      </UncontrolledTooltip>
                      <Row>
                        <Col md={12} className="mb-2">
                          <h6 className="mb-1">Directors</h6>
                          <small className="text-muted d-block mb-2">
                            ID upload must be a clear photo or scan. Proof of
                            address must be dated within the last 3 months.
                          </small>
                        </Col>

                        {validation.values.ownershipControlDeclaration.directors.map(
                          (dir, idx) => {
                            const touched =
                              validation.touched.ownershipControlDeclaration
                                ?.directors?.[idx] || {};
                            const errors =
                              validation.errors.ownershipControlDeclaration
                                ?.directors?.[idx] || {};
                            return (
                              <React.Fragment key={`dir-${idx}`}>
                                <Col md={12} className="mb-2">
                                  <strong>Director {idx + 1}</strong>
                                </Col>
                                <Col md={12} className="mb-3">
                                  <Label>Company director full name *</Label>
                                  <Input
                                    name={`ownershipControlDeclaration.directors[${idx}].fullName`}
                                    value={dir.fullName}
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    invalid={
                                      touched?.fullName && !!errors?.fullName
                                    }
                                    placeholder="Full name"
                                  />
                                  <FormFeedback>
                                    {errors?.fullName}
                                  </FormFeedback>
                                </Col>
                                <Col md={6} className="mb-3">
                                  <Label>
                                    Upload passport or driving licence *
                                  </Label>
                                  <Input
                                    type="file"
                                    accept=".pdf,image/*"
                                    name={`ownershipControlDeclaration.directors[${idx}].idFile`}
                                    className={
                                      touched?.idFile && errors?.idFile
                                        ? "is-invalid"
                                        : ""
                                    }
                                    onChange={(e) => {
                                      const f = e.currentTarget.files?.[0];
                                      setNested(
                                        `ownershipControlDeclaration.directors[${idx}].idFile`,
                                        f || null
                                      );
                                    }}
                                    onBlur={() =>
                                      touchNested(
                                        `ownershipControlDeclaration.directors[${idx}].idFile`
                                      )
                                    }
                                  />
                                  {dir.idFile instanceof File && (
                                    <small className="text-muted d-block mt-1">
                                      {dir.idFile.name}
                                    </small>
                                  )}
                                  {touched?.idFile && errors?.idFile && (
                                    <div className="invalid-feedback d-block">
                                      {errors.idFile}
                                    </div>
                                  )}
                                </Col>
                                <Col md={6} className="mb-3">
                                  <Label>
                                    Upload proof of address *{" "}
                                    <span
                                      className="text-muted"
                                      title="Must be dated within the last 3 months"
                                    >
                                      (last 3 months)
                                    </span>
                                  </Label>
                                  <Input
                                    type="file"
                                    accept=".pdf,image/*"
                                    name={`ownershipControlDeclaration.directors[${idx}].proofOfAddressFile`}
                                    className={
                                      touched?.proofOfAddressFile &&
                                      errors?.proofOfAddressFile
                                        ? "is-invalid"
                                        : ""
                                    }
                                    onChange={(e) => {
                                      const f = e.currentTarget.files?.[0];
                                      setNested(
                                        `ownershipControlDeclaration.directors[${idx}].proofOfAddressFile`,
                                        f || null
                                      );
                                    }}
                                    onBlur={() =>
                                      touchNested(
                                        `ownershipControlDeclaration.directors[${idx}].proofOfAddressFile`
                                      )
                                    }
                                  />
                                  {dir.proofOfAddressFile instanceof File && (
                                    <small className="text-muted d-block mt-1">
                                      {dir.proofOfAddressFile.name}
                                    </small>
                                  )}
                                  {touched?.proofOfAddressFile &&
                                    errors?.proofOfAddressFile && (
                                      <div className="invalid-feedback d-block">
                                        {errors.proofOfAddressFile}
                                      </div>
                                    )}
                                </Col>
                                <Col
                                  md={12}
                                  className="mb-2 d-flex justify-content-end"
                                >
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => removeDirector(idx)}
                                    disabled={
                                      validation.values
                                        .ownershipControlDeclaration.directors
                                        .length === 1
                                    }
                                  >
                                    Remove director
                                  </button>
                                </Col>
                                <Col md={12}>
                                  <hr className="my-2" />
                                </Col>
                              </React.Fragment>
                            );
                          }
                        )}
                        <Col md={12} className="mb-3">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={addDirector}
                          >
                            Add another director
                          </button>
                        </Col>

                        <Col md={12} className="mb-3">
                          <Label className="d-block mb-2">
                            Company ownership type *
                          </Label>
                          <div className="form-check">
                            <Input
                              id="own-individuals"
                              className="form-check-input"
                              type="radio"
                              checked={
                                validation.values.ownershipControlDeclaration
                                  .ownershipCompanyType === "individuals"
                              }
                              onChange={() =>
                                handleOwnershipTypeChange("individuals")
                              }
                              onBlur={() =>
                                validation.setFieldTouched(
                                  "ownershipControlDeclaration.ownershipCompanyType",
                                  true
                                )
                              }
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="own-individuals"
                            >
                              The company is owned by individual(s) only
                            </Label>
                          </div>
                          <div className="form-check">
                            <Input
                              id="own-corporate"
                              className="form-check-input"
                              type="radio"
                              checked={
                                validation.values.ownershipControlDeclaration
                                  .ownershipCompanyType === "corporate"
                              }
                              onChange={() =>
                                handleOwnershipTypeChange("corporate")
                              }
                              onBlur={() =>
                                validation.setFieldTouched(
                                  "ownershipControlDeclaration.ownershipCompanyType",
                                  true
                                )
                              }
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="own-corporate"
                            >
                              The company is owned by a corporate entity
                            </Label>
                          </div>
                          <div className="invalid-feedback d-block">
                            {validation.touched.ownershipControlDeclaration
                              ?.ownershipCompanyType &&
                              validation.errors.ownershipControlDeclaration
                                ?.ownershipCompanyType}
                          </div>
                        </Col>

                        {validation.values.ownershipControlDeclaration
                          .ownershipCompanyType === "individuals" && (
                          <>
                            <Col md={12} className="mb-2 mt-2">
                              <h6 className="mb-0">
                                Individuals with 25% or more ownership
                              </h6>
                              <small className="text-muted d-block">
                                List each individual who holds 25% or more. ID
                                must be clear; proof of address within the last
                                3 months.
                              </small>
                            </Col>
                            {validation.values.ownershipControlDeclaration.individualOwners.map(
                              (row, idx) => {
                                const touched =
                                  validation.touched.ownershipControlDeclaration
                                    ?.individualOwners?.[idx] || {};
                                const errors =
                                  validation.errors.ownershipControlDeclaration
                                    ?.individualOwners?.[idx] || {};
                                return (
                                  <React.Fragment key={`ind-${idx}`}>
                                    <Col md={6} className="mb-3">
                                      <Label>Full name *</Label>
                                      <Input
                                        name={`ownershipControlDeclaration.individualOwners[${idx}].fullName`}
                                        value={row.fullName}
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        invalid={
                                          touched?.fullName &&
                                          !!errors?.fullName
                                        }
                                      />
                                      <FormFeedback>
                                        {errors?.fullName}
                                      </FormFeedback>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                      <Label>Ownership % *</Label>
                                      <Input
                                        type="number"
                                        min={25}
                                        max={getMaxAllowedOwnershipPercent(
                                          validation.values
                                            .ownershipControlDeclaration
                                            .individualOwners,
                                          idx
                                        )}
                                        step="1"
                                        name={`ownershipControlDeclaration.individualOwners[${idx}].percentageOfOwnership`}
                                        value={row.percentageOfOwnership}
                                        onChange={(e) =>
                                          setOwnerPercentage(
                                            `ownershipControlDeclaration.individualOwners[${idx}].percentageOfOwnership`,
                                            e.target.value
                                          )
                                        }
                                        onBlur={validation.handleBlur}
                                        invalid={
                                          touched?.percentageOfOwnership &&
                                          !!errors?.percentageOfOwnership
                                        }
                                      />
                                      <FormFeedback>
                                        {errors?.percentageOfOwnership}
                                      </FormFeedback>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                      <Label>
                                        Upload passport or driving licence *
                                      </Label>
                                      <Input
                                        type="file"
                                        accept=".pdf,image/*"
                                        className={
                                          touched?.idFile && errors?.idFile
                                            ? "is-invalid"
                                            : ""
                                        }
                                        onChange={(e) => {
                                          const f = e.currentTarget.files?.[0];
                                          setNested(
                                            `ownershipControlDeclaration.individualOwners[${idx}].idFile`,
                                            f || null
                                          );
                                        }}
                                        onBlur={() =>
                                          touchNested(
                                            `ownershipControlDeclaration.individualOwners[${idx}].idFile`
                                          )
                                        }
                                      />
                                      {row.idFile instanceof File && (
                                        <small className="text-muted d-block mt-1">
                                          {row.idFile.name}
                                        </small>
                                      )}
                                      {touched?.idFile && errors?.idFile && (
                                        <div className="invalid-feedback d-block">
                                          {errors.idFile}
                                        </div>
                                      )}
                                    </Col>
                                    <Col md={6} className="mb-3">
                                      <Label>
                                        Upload proof of address *{" "}
                                        <span
                                          className="text-muted"
                                          title="Must be dated within the last 3 months"
                                        >
                                          (last 3 months)
                                        </span>
                                      </Label>
                                      <Input
                                        type="file"
                                        accept=".pdf,image/*"
                                        className={
                                          touched?.proofOfAddressFile &&
                                          errors?.proofOfAddressFile
                                            ? "is-invalid"
                                            : ""
                                        }
                                        onChange={(e) => {
                                          const f = e.currentTarget.files?.[0];
                                          setNested(
                                            `ownershipControlDeclaration.individualOwners[${idx}].proofOfAddressFile`,
                                            f || null
                                          );
                                        }}
                                        onBlur={() =>
                                          touchNested(
                                            `ownershipControlDeclaration.individualOwners[${idx}].proofOfAddressFile`
                                          )
                                        }
                                      />
                                      {row.proofOfAddressFile instanceof
                                        File && (
                                        <small className="text-muted d-block mt-1">
                                          {row.proofOfAddressFile.name}
                                        </small>
                                      )}
                                      {touched?.proofOfAddressFile &&
                                        errors?.proofOfAddressFile && (
                                          <div className="invalid-feedback d-block">
                                            {errors.proofOfAddressFile}
                                          </div>
                                        )}
                                    </Col>
                                    <Col
                                      md={12}
                                      className="mb-2 d-flex justify-content-end"
                                    >
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() =>
                                          removeIndividualOwner(idx)
                                        }
                                        disabled={
                                          validation.values
                                            .ownershipControlDeclaration
                                            .individualOwners.length === 1
                                        }
                                      >
                                        Remove
                                      </button>
                                    </Col>
                                    <Col md={12}>
                                      <hr className="my-2" />
                                    </Col>
                                  </React.Fragment>
                                );
                              }
                            )}
                            <Col md={12} className="mb-2">
                              <small className="text-muted">
                                Total ownership:{" "}
                                {sumOwnershipPercentages(
                                  validation.values.ownershipControlDeclaration
                                    .individualOwners
                                )}
                                % / 100%
                              </small>
                            </Col>
                            <Col md={12} className="mb-3">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={addIndividualOwner}
                              >
                                Add another
                              </button>
                            </Col>
                            {typeof validation.errors
                              .ownershipControlDeclaration?.individualOwners ===
                              "string" && (
                              <Col md={12} className="mb-3">
                                <div className="invalid-feedback d-block">
                                  {
                                    validation.errors
                                      .ownershipControlDeclaration
                                      .individualOwners
                                  }
                                </div>
                              </Col>
                            )}
                          </>
                        )}

                        {validation.values.ownershipControlDeclaration
                          .ownershipCompanyType === "corporate" && (
                          <>
                            <Col md={12} className="mb-3">
                              <Label>Company ownership structure (PDF) *</Label>
                              <Input
                                type="file"
                                accept=".pdf,application/pdf"
                                className={
                                  validation.touched.ownershipControlDeclaration
                                    ?.companyOwnershipStructurePDF &&
                                  validation.errors.ownershipControlDeclaration
                                    ?.companyOwnershipStructurePDF
                                    ? "is-invalid"
                                    : ""
                                }
                                onChange={(e) => {
                                  const f = e.currentTarget.files?.[0];
                                  setNested(
                                    "ownershipControlDeclaration.companyOwnershipStructurePDF",
                                    f || null
                                  );
                                }}
                                onBlur={() =>
                                  touchNested(
                                    "ownershipControlDeclaration.companyOwnershipStructurePDF"
                                  )
                                }
                              />
                              {validation.values.ownershipControlDeclaration
                                .companyOwnershipStructurePDF instanceof
                                File && (
                                <small className="text-muted d-block mt-1">
                                  {
                                    validation.values
                                      .ownershipControlDeclaration
                                      .companyOwnershipStructurePDF.name
                                  }
                                </small>
                              )}
                              {validation.touched.ownershipControlDeclaration
                                ?.companyOwnershipStructurePDF &&
                                validation.errors.ownershipControlDeclaration
                                  ?.companyOwnershipStructurePDF && (
                                  <div className="invalid-feedback d-block">
                                    {
                                      validation.errors
                                        .ownershipControlDeclaration
                                        .companyOwnershipStructurePDF
                                    }
                                  </div>
                                )}
                            </Col>
                            <Col md={12} className="mb-2 mt-2">
                              <h6 className="mb-0">
                                Ultimate shareholders (25% or more)
                              </h6>
                              <small className="text-muted d-block">
                                Full name of each ultimate shareholder with 25%
                                or more holding. ID must be clear; proof of
                                address within the last 3 months.
                              </small>
                            </Col>
                            {validation.values.ownershipControlDeclaration.corporateUltimateOwners.map(
                              (row, idx) => {
                                const touched =
                                  validation.touched.ownershipControlDeclaration
                                    ?.corporateUltimateOwners?.[idx] || {};
                                const errors =
                                  validation.errors.ownershipControlDeclaration
                                    ?.corporateUltimateOwners?.[idx] || {};
                                return (
                                  <React.Fragment key={`corp-${idx}`}>
                                    <Col md={6} className="mb-3">
                                      <Label>
                                        Full name of ultimate shareholder *
                                      </Label>
                                      <Input
                                        name={`ownershipControlDeclaration.corporateUltimateOwners[${idx}].fullName`}
                                        value={row.fullName}
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        invalid={
                                          touched?.fullName &&
                                          !!errors?.fullName
                                        }
                                      />
                                      <FormFeedback>
                                        {errors?.fullName}
                                      </FormFeedback>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                      <Label>Ownership % *</Label>
                                      <Input
                                        type="number"
                                        min={25}
                                        max={getMaxAllowedOwnershipPercent(
                                          validation.values
                                            .ownershipControlDeclaration
                                            .corporateUltimateOwners,
                                          idx
                                        )}
                                        step="1"
                                        name={`ownershipControlDeclaration.corporateUltimateOwners[${idx}].percentageOfOwnership`}
                                        value={row.percentageOfOwnership}
                                        onChange={(e) =>
                                          setOwnerPercentage(
                                            `ownershipControlDeclaration.corporateUltimateOwners[${idx}].percentageOfOwnership`,
                                            e.target.value
                                          )
                                        }
                                        onBlur={validation.handleBlur}
                                        invalid={
                                          touched?.percentageOfOwnership &&
                                          !!errors?.percentageOfOwnership
                                        }
                                      />
                                      <FormFeedback>
                                        {errors?.percentageOfOwnership}
                                      </FormFeedback>
                                    </Col>
                                    <Col md={6} className="mb-3">
                                      <Label>
                                        Upload passport or driving licence *
                                      </Label>
                                      <Input
                                        type="file"
                                        accept=".pdf,image/*"
                                        className={
                                          touched?.idFile && errors?.idFile
                                            ? "is-invalid"
                                            : ""
                                        }
                                        onChange={(e) => {
                                          const f = e.currentTarget.files?.[0];
                                          setNested(
                                            `ownershipControlDeclaration.corporateUltimateOwners[${idx}].idFile`,
                                            f || null
                                          );
                                        }}
                                        onBlur={() =>
                                          touchNested(
                                            `ownershipControlDeclaration.corporateUltimateOwners[${idx}].idFile`
                                          )
                                        }
                                      />
                                      {row.idFile instanceof File && (
                                        <small className="text-muted d-block mt-1">
                                          {row.idFile.name}
                                        </small>
                                      )}
                                      {touched?.idFile && errors?.idFile && (
                                        <div className="invalid-feedback d-block">
                                          {errors.idFile}
                                        </div>
                                      )}
                                    </Col>
                                    <Col md={6} className="mb-3">
                                      <Label>
                                        Upload proof of address *{" "}
                                        <span
                                          className="text-muted"
                                          title="Must be dated within the last 3 months"
                                        >
                                          (last 3 months)
                                        </span>
                                      </Label>
                                      <Input
                                        type="file"
                                        accept=".pdf,image/*"
                                        className={
                                          touched?.proofOfAddressFile &&
                                          errors?.proofOfAddressFile
                                            ? "is-invalid"
                                            : ""
                                        }
                                        onChange={(e) => {
                                          const f = e.currentTarget.files?.[0];
                                          setNested(
                                            `ownershipControlDeclaration.corporateUltimateOwners[${idx}].proofOfAddressFile`,
                                            f || null
                                          );
                                        }}
                                        onBlur={() =>
                                          touchNested(
                                            `ownershipControlDeclaration.corporateUltimateOwners[${idx}].proofOfAddressFile`
                                          )
                                        }
                                      />
                                      {row.proofOfAddressFile instanceof
                                        File && (
                                        <small className="text-muted d-block mt-1">
                                          {row.proofOfAddressFile.name}
                                        </small>
                                      )}
                                      {touched?.proofOfAddressFile &&
                                        errors?.proofOfAddressFile && (
                                          <div className="invalid-feedback d-block">
                                            {errors.proofOfAddressFile}
                                          </div>
                                        )}
                                    </Col>
                                    <Col
                                      md={12}
                                      className="mb-2 d-flex justify-content-end"
                                    >
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() =>
                                          removeCorporateUltimateOwner(idx)
                                        }
                                        disabled={
                                          validation.values
                                            .ownershipControlDeclaration
                                            .corporateUltimateOwners.length ===
                                          1
                                        }
                                      >
                                        Remove
                                      </button>
                                    </Col>
                                    <Col md={12}>
                                      <hr className="my-2" />
                                    </Col>
                                  </React.Fragment>
                                );
                              }
                            )}
                            <Col md={12} className="mb-2">
                              <small className="text-muted">
                                Total ownership:{" "}
                                {sumOwnershipPercentages(
                                  validation.values.ownershipControlDeclaration
                                    .corporateUltimateOwners
                                )}
                                % / 100%
                              </small>
                            </Col>
                            <Col md={12} className="mb-3">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={addCorporateUltimateOwner}
                              >
                                Add another
                              </button>
                            </Col>
                            {typeof validation.errors
                              .ownershipControlDeclaration
                              ?.corporateUltimateOwners === "string" && (
                              <Col md={12} className="mb-3">
                                <div className="invalid-feedback d-block">
                                  {
                                    validation.errors
                                      .ownershipControlDeclaration
                                      .corporateUltimateOwners
                                  }
                                </div>
                              </Col>
                            )}
                          </>
                        )}

                        <Col md={12} className="mb-3">
                          <div className="form-check">
                            <Input
                              id="occ-confirmed"
                              className="form-check-input"
                              type="checkbox"
                              checked={
                                validation.values.ownershipControlDeclaration
                                  .confirmed
                              }
                              onChange={(e) =>
                                setNested(
                                  "ownershipControlDeclaration.confirmed",
                                  e.target.checked
                                )
                              }
                              onBlur={() =>
                                validation.setFieldTouched(
                                  "ownershipControlDeclaration.confirmed",
                                  true
                                )
                              }
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="occ-confirmed"
                            >
                              I confirm the above ownership/control details are
                              accurate. *
                            </Label>
                          </div>
                          <div className="invalid-feedback d-block">
                            {validation.touched.ownershipControlDeclaration
                              ?.confirmed &&
                              validation.errors.ownershipControlDeclaration
                                ?.confirmed}
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* ============ Declaration & consent ============ */}
                  <Card
                    className="mb-3"
                    style={{
                      backgroundColor: "#FFFFFF",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      borderRadius: "6px",
                      border: "1px solid #E0E0E0",
                    }}
                  >
                    <CardHeader
                      style={{
                        backgroundColor: "#E8E8E8",
                        borderBottom: "1px solid #E0E0E0",
                      }}
                    >
                      <h5
                        className="mb-0"
                        style={{
                          fontSize: "18px",
                          fontWeight: "bold",
                          color: "#333333",
                        }}
                      >
                        Declaration & consent
                      </h5>
                    </CardHeader>
                    <CardBody style={{ backgroundColor: "#F5F5F5" }}>
                      <Row>
                        <Col md={12} className="mb-3">
                          <div className="form-check">
                            <Input
                              id="dac-confirm-accurate"
                              className="form-check-input"
                              type="checkbox"
                              checked={
                                validation.values.declarationAndConsent
                                  .confirmInformationProvidedIsTrueAndAccurate
                              }
                              onChange={(e) =>
                                setNested(
                                  "declarationAndConsent.confirmInformationProvidedIsTrueAndAccurate",
                                  e.target.checked
                                )
                              }
                              onBlur={() =>
                                validation.setFieldTouched(
                                  "declarationAndConsent.confirmInformationProvidedIsTrueAndAccurate",
                                  true
                                )
                              }
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="dac-confirm-accurate"
                            >
                              I confirm the information provided is true and
                              accurate. *
                            </Label>
                          </div>
                          <div className="invalid-feedback d-block">
                            {validation.touched.declarationAndConsent
                              ?.confirmInformationProvidedIsTrueAndAccurate &&
                              validation.errors.declarationAndConsent
                                ?.confirmInformationProvidedIsTrueAndAccurate}
                          </div>
                        </Col>
                        <Col md={12} className="mb-3">
                          <div className="form-check">
                            <Input
                              id="dac-agree-terms"
                              className="form-check-input"
                              type="checkbox"
                              checked={
                                validation.values.declarationAndConsent
                                  .agreeTermsAndPrivacyNotice
                              }
                              onChange={(e) =>
                                setNested(
                                  "declarationAndConsent.agreeTermsAndPrivacyNotice",
                                  e.target.checked
                                )
                              }
                              onBlur={() =>
                                validation.setFieldTouched(
                                  "declarationAndConsent.agreeTermsAndPrivacyNotice",
                                  true
                                )
                              }
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="dac-agree-terms"
                            >
                              I agree to the terms and{" "}
                              <a
                                href="https://www.jobark.co.uk/privacy-policy/"
                                target="_blank"
                                rel="noreferrer"
                              >
                                privacy policy
                              </a>
                              . *
                            </Label>
                          </div>
                          <div className="invalid-feedback d-block">
                            {validation.touched.declarationAndConsent
                              ?.agreeTermsAndPrivacyNotice &&
                              validation.errors.declarationAndConsent
                                ?.agreeTermsAndPrivacyNotice}
                          </div>
                        </Col>
                        <Col md={12} className="mb-3">
                          <div className="form-check">
                            <Input
                              id="dac-marketing"
                              className="form-check-input"
                              type="checkbox"
                              checked={
                                validation.values.declarationAndConsent
                                  .consentToReceivingMarketingInformation
                              }
                              onChange={(e) =>
                                setNested(
                                  "declarationAndConsent.consentToReceivingMarketingInformation",
                                  e.target.checked
                                )
                              }
                              onBlur={() =>
                                validation.setFieldTouched(
                                  "declarationAndConsent.consentToReceivingMarketingInformation",
                                  true
                                )
                              }
                            />
                            <Label
                              className="form-check-label"
                              htmlFor="dac-marketing"
                            >
                              I consent to receiving marketing information
                              (optional).
                            </Label>
                          </div>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  <div className="d-grid" style={{ paddingBottom: "20px" }}>
                    <button
                      className="btn btn-secondary btn-block"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </Form>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </React.Fragment>
  );
};

ClientOnBoarding.propTypes = {
  router: PropTypes.shape({
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string
    }),
    navigate: PropTypes.func
  })
};

const ClientOnBoardingWithRouter = withRouter(ClientOnBoarding);
export default ClientOnBoardingWithRouter;
